// ===========================================
// SESSION TIMER - ESTILO VFP9 + BANCARIO
// "CONSTRUYENDO UN NUEVO MUNDO SOBRE BASE SÓLIDA"
// ===========================================
// Timer automático que valida sesión en background
// Similar a Timer1.Timer en Visual FoxPro 9
// Comportamiento bancario: logout automático sin preguntar
// ===========================================

import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { secureFetch } from './authInterceptor';

// ===========================================
// CONFIGURACIÓN DEL TIMER
// ===========================================
const TIMER_CONFIG = {
  // Intervalo de verificación (milisegundos)
  CHECK_INTERVAL: 3600000,  // ← 1 hora estándar industrial
  
  // Tiempo de espera antes de logout (milisegundos)
  LOGOUT_DELAY: 2000,           // 2 segundos - Estilo bancario
  
  // Mensaje al expirar
  EXPIRATION_MESSAGE: 'Sesión expirada por seguridad', ////////////////.//////???????
  
  // Endpoint de verificación
  VERIFY_ENDPOINT: '/api/auth/verify',
  
  // LocalStorage keys
  TOKEN_KEY: 'mapasac_access_token',
  REFRESH_TOKEN_KEY: 'mapasac_refresh_token',
  SESSION_KEY: 'mapasac_session',
  
  // Debug mode (activar para ver logs)
  DEBUG: false
};

// ===========================================
// CUSTOM HOOK: useSessionTimer
// ===========================================
/**
 * Hook personalizado para validar sesión automáticamente
 * Funciona como Timer de VFP9 + comportamiento bancario
 * 
 * @param {Function} onSessionExpired - Callback al expirar sesión (opcional)
 * 
 * @example
 * // En tu componente:
 * function Dashboard({ onLogout }) {
 *   useSessionTimer(onLogout);
 *   return <div>Dashboard</div>;
 * }
 */
export const useSessionTimer = (onSessionExpired) => {
  useEffect(() => {
    // Log inicio del timer (si debug activado)
    //if (TIMER_CONFIG.DEBUG) {
    //  console.log(`✅ Timer de sesión iniciado (cada ${TIMER_CONFIG.CHECK_INTERVAL / 1000}s)`);
    //}
    
    // ===========================================
    // TIMER PRINCIPAL (Estilo VFP9: Timer1.Timer)
    // ===========================================
    const sessionTimer = setInterval(async () => {
      //if (TIMER_CONFIG.DEBUG) {
      //  console.log('🔍 Verificando token...', new Date().toLocaleTimeString());
      //}
      
      try {
        // Obtener token del localStorage
        const token = sessionStorage.getItem(TIMER_CONFIG.TOKEN_KEY);

        // Si no hay token, expirar inmediatamente
        if (!token) {
          if (TIMER_CONFIG.DEBUG) {
            console.warn('❌ No hay token en localStorage');
          }
          handleSessionExpired();
          return;
        }
        
        // Verificar token con el backend
        const response = await secureFetch(TIMER_CONFIG.VERIFY_ENDPOINT, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        // Si el token es inválido o expirado (401)
        if (!response.ok) {
          if (TIMER_CONFIG.DEBUG) {
            console.warn('❌ Token inválido o expirado', response.status);
          }
          handleSessionExpired();
        } else {
          if (TIMER_CONFIG.DEBUG) {
            //console.log('✅ Token válido');
          }
        }
      } catch (error) {
        // Error de red o servidor
        console.error('⚠️ Error verificando sesión:', error.message);
        // NO cerrar sesión por errores de red temporales
        // Solo cerrar si el token realmente expiró (401)
      }
    }, TIMER_CONFIG.CHECK_INTERVAL);
    
    // ===========================================
    // FUNCIÓN DE EXPIRACIÓN (Estilo Bancario)
    // ===========================================
    const handleSessionExpired = () => {
      // Detener el timer inmediatamente
      clearInterval(sessionTimer);
      
      //if (TIMER_CONFIG.DEBUG) {
      //  console.log('🚪 Cerrando sesión automáticamente...');
      //}
      
      // Mostrar toast informativo (NO pregunta, solo informa)
      toast.warning(TIMER_CONFIG.EXPIRATION_MESSAGE, {
        autoClose: TIMER_CONFIG.LOGOUT_DELAY,
        closeButton: false,
        draggable: false
      });
      
      // Esperar y luego cerrar sesión (estilo bancario)
      setTimeout(() => {
        // Limpiar sessionStorage
        sessionStorage.removeItem(TIMER_CONFIG.TOKEN_KEY);
        sessionStorage.removeItem(TIMER_CONFIG.REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(TIMER_CONFIG.SESSION_KEY);
        
        //if (TIMER_CONFIG.DEBUG) {
        //  console.log('🗑️ LocalStorage limpiado');
        //}
        
        // Ejecutar callback si existe
        if (onSessionExpired && typeof onSessionExpired === 'function') {
          //if (TIMER_CONFIG.DEBUG) {
          //  console.log('📞 Ejecutando callback onSessionExpired');
          //}
          onSessionExpired();
        } else {
          // Si no hay callback, redirigir a inicio
          //if (TIMER_CONFIG.DEBUG) {
          //  console.log('🔄 Redirigiendo a login...');
          //}
          window.location.href = '/';
        }
      }, TIMER_CONFIG.LOGOUT_DELAY);
    };
    
    // ===========================================
    // CLEANUP (Como THISFORM.Release() en VFP9)
    // ===========================================
    return () => {
      clearInterval(sessionTimer);
      //if (TIMER_CONFIG.DEBUG) {
      //  console.log('🛑 Timer de sesión detenido');
      //}
    };
    
  }, [onSessionExpired]); // Re-crear si cambia el callback
};

// ===========================================
// FUNCIÓN AUXILIAR: Verificar token manualmente
// ===========================================
/**
 * Verifica token inmediatamente (sin esperar al timer)
 * Útil para verificar antes de operaciones críticas
 * 
 * @returns {Promise<boolean>} true si token es válido
 * 
 * @example
 * const isValid = await verifyTokenNow();
 * if (!isValid) {
 *   // Token expirado
 * }
 */
export const verifyTokenNow = async () => {
  try {
    const token = sessionStorage.getItem(TIMER_CONFIG.TOKEN_KEY);
    
    if (!token) {
      return false;
    }
    
    const response = await secureFetch(TIMER_CONFIG.VERIFY_ENDPOINT, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error verificando token:', error);
    return false;
  }
};

// ===========================================
// FUNCIÓN AUXILIAR: Configurar timer
// ===========================================
/**
 * Permite modificar configuración del timer en runtime
 * Útil para testing o ajustes específicos
 * 
 * @param {Object} newConfig - Configuración parcial a actualizar
 * 
 * @example
 * configureTimer({ CHECK_INTERVAL: 15000, DEBUG: true });
 */
export const configureTimer = (newConfig) => {
  Object.assign(TIMER_CONFIG, newConfig);
  
  //if (TIMER_CONFIG.DEBUG) {
  //  console.log('⚙️ Timer reconfigurado:', TIMER_CONFIG);
  //}
};

// ===========================================
// EXPORTAR CONFIGURACIÓN (para lectura)
// ===========================================
export const getTimerConfig = () => ({ ...TIMER_CONFIG });

// ===========================================
// NOTAS DE USO:
// ===========================================
/*
EJEMPLO BÁSICO:
---------------
import { useSessionTimer } from './utils/sessionTimer';

function Dashboard({ onLogout }) {
  useSessionTimer(onLogout);
  return <div>Content</div>;
}

EJEMPLO CON VERIFICACIÓN MANUAL:
--------------------------------
import { useSessionTimer, verifyTokenNow } from './utils/sessionTimer';

function CriticalOperation() {
  useSessionTimer(onLogout);
  
  const handleCriticalAction = async () => {
    const isValid = await verifyTokenNow();
    if (!isValid) {
      alert('Token expirado, no se puede continuar');
      return;
    }
    // Proceder con operación crítica
  };
}

EJEMPLO CON DEBUG:
------------------
import { useSessionTimer, configureTimer } from './utils/sessionTimer';

// Activar debug mode
configureTimer({ DEBUG: true });

function Dashboard({ onLogout }) {
  useSessionTimer(onLogout);
  // Verás logs en consola de cada verificación
}

CAMBIAR INTERVALO (TESTING):
-----------------------------
import { configureTimer } from './utils/sessionTimer';

// Para testing rápido (cada 5 segundos)
configureTimer({ CHECK_INTERVAL: 5000 });
*/